import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Dna, Stethoscope, Search, Globe, ChevronRight, Activity } from 'lucide-react';

const JohnsHopkinsUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Johns Hopkins University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#333] selection:bg-[#002d72] selection:text-white">
             {/* Header */}
            <nav className="bg-white px-6 py-6 border-b border-gray-200">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 border-2 border-[#002d72] rounded-sm flex items-center justify-center text-[#002d72] font-serif font-bold text-xl">
                            JH
                        </div>
                        <div className="flex flex-col">
                            <span className="uppercase text-xl font-serif text-[#002d72] leading-none tracking-wide">{schoolName}</span>
                        </div>
                    </div>

                    <div className="flex gap-8 font-bold text-sm text-[#002d72] uppercase tracking-wide items-center">
                        <a href="#" className="hover:underline">About</a>
                        <a href="#" className="hover:underline">Admissions</a>
                        <a href="#" className="hover:underline">Academics</a>
                        <a href="#" className="hover:underline">Research</a>
                        <Search size={20} className="text-[#002d72] cursor-pointer" />
                    </div>
                </div>
            </nav>

            {/* Hero Section - Blue Overlay */}
            <header className="relative bg-[#002d72] text-white">
                <div className="grid grid-cols-1 lg:grid-cols-2 h-[600px]">
                    <div className="p-12 md:p-20 flex flex-col justify-center">
                         <h1 className="text-5xl md:text-6xl font-serif mb-8 leading-tight">
                             Knowledge for the world.
                         </h1>
                         <p className="text-xl text-blue-100 max-w-lg mb-12 font-light">
                             America's first research university. We believe in the power of research and discovery to improve lives.
                         </p>
                         <div className="flex gap-4">
                             <Button className="bg-white text-[#002d72] hover:bg-gray-100 font-bold uppercase tracking-widest px-8 h-12 rounded-sm">
                                 Explore Programs
                             </Button>
                         </div>
                    </div>
                    <div className="relative h-full w-full">
                         <img 
                            src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=2080&auto=format&fit=crop" 
                            alt="Lab Research" 
                            className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-50"
                         />
                         <div className="absolute inset-0 bg-gradient-to-r from-[#002d72] to-transparent"></div>
                    </div>
                </div>
            </header>

            {/* Stats Bar */}
            <div className="bg-[#68ace5] py-12 px-6">
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-[#002d72]">
                    {[
                        { val: "#1", label: "In Research Spending" },
                        { val: "29", label: "Nobel Laureates" },
                        { val: "3:1", label: "Undergrad Research" },
                        { val: "9 Campuses", label: "Global Reach" }
                    ].map((stat, i) => (
                        <div key={i} className="flex flex-col items-center">
                            <span className="text-4xl font-bold font-serif mb-1">{stat.val}</span>
                            <span className="text-xs font-bold uppercase tracking-widest">{stat.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* News / Research Highlights */}
            <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-12">
                     <h2 className="text-3xl font-serif text-[#002d72]">{schoolName} in the News</h2>
                     <a href="#" className="text-[#002d72] font-bold flex items-center gap-2 hover:underline">See All Stories <ChevronRight size={18}/></a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="md:col-span-2 relative group cursor-pointer h-[500px]">
                        <img src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover rounded-sm filter brightness-75 group-hover:brightness-100 transition-all" alt="Main Story" />
                        <div className="absolute bottom-0 left-0 p-8 text-white w-full bg-gradient-to-t from-black/80 to-transparent">
                             <div className="bg-[#002d72] text-white text-xs font-bold uppercase tracking-widest inline-block px-2 py-1 mb-4">Public Health</div>
                             <h3 className="text-3xl font-bold font-serif leading-tight">
                                 Global health experts tracking new virus variants
                             </h3>
                        </div>
                    </div>
                    
                    <div className="flex flex-col gap-8">
                         {[
                             { icon: Dna, date: "Oct 22", title: "Genomic study maps human diversity" },
                             { icon: Activity, date: "Oct 20", title: "New robotic surgery technique proves successful" },
                             { icon: Globe, date: "Oct 18", title: "International relations students meet UN delegates" }
                         ].map((item, i) => (
                             <div key={i} className="flex gap-4 p-6 border border-gray-200 rounded-sm hover:shadow-md transition-shadow cursor-pointer bg-white h-full">
                                 <div className="shrink-0 text-[#68ace5]">
                                     <item.icon size={32} />
                                 </div>
                                 <div>
                                     <div className="text-xs font-bold text-gray-500 uppercase mb-2">{item.date}</div>
                                     <h4 className="text-lg font-bold text-[#002d72] leading-tight group-hover:underline">{item.title}</h4>
                                 </div>
                             </div>
                         ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#white] border-t-4 border-[#002d72] pt-16 pb-12 px-6">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 text-sm text-gray-600">
                    <div className="col-span-1 md:col-span-2">
                         <div className="flex flex-col mb-6">
                            <span className="uppercase text-2xl font-serif text-[#002d72] leading-none tracking-wide">{schoolName}</span>
                        </div>
                        <p className="max-w-xs leading-relaxed">
                            3400 N. Charles Street <br/>
                            Baltimore, MD 21218 <br/>
                            (410) 516-8000
                        </p>
                    </div>

                    <div>
                        <h4 className="font-bold text-[#002d72] uppercase tracking-widest text-xs mb-6">Divisions</h4>
                        <ul className="space-y-3">
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Arts & Sciences</a></li>
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Engineering</a></li>
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Medicine</a></li>
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Nursing</a></li>
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Peabody Institute</a></li>
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Public Health</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-bold text-[#002d72] uppercase tracking-widest text-xs mb-6">Information</h4>
                        <ul className="space-y-3">
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Contact</a></li>
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Maps & Directions</a></li>
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Emergency Info</a></li>
                            <li><a href="#" className="hover:text-[#002d72] hover:underline">Careers</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between text-xs text-gray-500">
                     <p>© 2026 {schoolName}. All rights reserved.</p>
                     <div className="flex gap-6 mt-4 md:mt-0">
                         <a href="#" className="hover:text-[#002d72]">Privacy</a>
                         <a href="#" className="hover:text-[#002d72]">Accessibility</a>
                     </div>
                </div>
            </footer>
        </div>
    );
};

export default JohnsHopkinsUniversity;
